import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Eye, Heart, MessageCircle, Share2, Users, Loader2, AlertTriangle, RefreshCw, Youtube, Facebook, Instagram, Linkedin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getIntegrations, getAnalytics } from '../../api/socialMedia';
import { PLATFORM_COLORS, PLATFORMS } from '../../utils/socialMediaHelpers';

const PLATFORM_ICONS = {
    youtube: Youtube,
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
};

const TIME_PERIODS = [
    { value: 7, label: '7 days' },
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
];

const MetricCard = ({ label, value, change, icon: Icon, loading }) => {
    const isPositive = change > 0;
    const isNegative = change < 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
        >
            <div className="flex items-start justify-between mb-4">
                <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-700">
                    <Icon className="w-6 h-6 text-slate-600 dark:text-slate-300" />
                </div>
                {change !== undefined && !loading && (
                    <div className={`flex items-center gap-1 text-sm font-medium ${
                        isPositive ? 'text-emerald-600 dark:text-emerald-400' :
                        isNegative ? 'text-red-600 dark:text-red-400' :
                        'text-slate-500 dark:text-slate-400'
                    }`}>
                        {isPositive && <TrendingUp className="w-4 h-4" />}
                        {isNegative && <TrendingDown className="w-4 h-4" />}
                        {change !== 0 && `${isPositive ? '+' : ''}${change.toFixed(1)}%`}
                        {change === 0 && '0%'}
                    </div>
                )}
            </div>
            <div>
                {loading ? (
                    <div className="h-9 w-24 bg-slate-100 dark:bg-slate-700 rounded animate-pulse" />
                ) : (
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </p>
                )}
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {label}
                </p>
            </div>
        </motion.div>
    );
};

const SocialAnalytics = () => {
    // State
    const [integrations, setIntegrations] = useState([]);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [timePeriod, setTimePeriod] = useState(30);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load integrations
    useEffect(() => {
        const loadIntegrations = async () => {
            try {
                const response = await getIntegrations();
                const data = response.data || [];
                setIntegrations(data);
                if (data.length > 0 && !selectedIntegration) {
                    setSelectedIntegration(data[0].id);
                }
            } catch (err) {
                console.error('Failed to load integrations:', err);
            }
        };
        loadIntegrations();
    }, []);

    // Load analytics
    const loadAnalytics = useCallback(async () => {
        if (!selectedIntegration) {
            setLoading(false);
            return;
        }

        try {
            setError(null);
            setLoading(true);

            const response = await getAnalytics(selectedIntegration, null, { days: timePeriod });
            setAnalytics(response.data);
        } catch (err) {
            console.error('Failed to load analytics:', err);
            setError(err.message || 'Failed to load analytics');
            // Set mock data for demo purposes
            setAnalytics({
                views: Math.floor(Math.random() * 50000),
                views_change: (Math.random() * 20) - 10,
                likes: Math.floor(Math.random() * 5000),
                likes_change: (Math.random() * 20) - 10,
                comments: Math.floor(Math.random() * 1000),
                comments_change: (Math.random() * 20) - 10,
                shares: Math.floor(Math.random() * 500),
                shares_change: (Math.random() * 20) - 10,
                followers: Math.floor(Math.random() * 10000),
                followers_change: (Math.random() * 10) - 5,
                engagement_rate: (Math.random() * 10).toFixed(2),
                engagement_change: (Math.random() * 5) - 2.5,
            });
        } finally {
            setLoading(false);
        }
    }, [selectedIntegration, timePeriod]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    // Get selected integration details
    const integration = integrations.find(i => i.id === selectedIntegration);
    const platform = integration?.platform;
    const Icon = PLATFORM_ICONS[platform] || Eye;
    const colors = PLATFORM_COLORS[platform];

    return (
        <div className="p-6 lg:p-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                        Analytics Overview
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Track your social media performance
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={loadAnalytics}
                        disabled={loading}
                        className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                {/* Integration selector */}
                <select
                    value={selectedIntegration || ''}
                    onChange={(e) => setSelectedIntegration(e.target.value || null)}
                    className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                    <option value="" disabled>Select an account</option>
                    {integrations.map((integration) => {
                        const IntIcon = PLATFORM_ICONS[integration.platform];
                        return (
                            <option key={integration.id} value={integration.id}>
                                {PLATFORMS[integration.platform]?.name || integration.platform} - {integration.account_name}
                            </option>
                        );
                    })}
                </select>

                {/* Time period tabs */}
                <div className="flex rounded-xl bg-slate-100 dark:bg-slate-800 p-1">
                    {TIME_PERIODS.map((period) => (
                        <button
                            key={period.value}
                            onClick={() => setTimePeriod(period.value)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                timePeriod === period.value
                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            {period.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Error display */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 flex items-start gap-3"
                    >
                        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm text-yellow-700 dark:text-yellow-400">{error}</p>
                            <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
                                Showing sample data for demonstration
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Empty state */}
            {integrations.length === 0 && !loading && (
                <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <TrendingUp className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                        No accounts connected
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400">
                        Connect a social media account to view analytics
                    </p>
                </div>
            )}

            {/* Metrics grid */}
            {(integration || loading) && (
                <>
                    {/* Selected account info */}
                    {integration && (
                        <div className={`p-4 rounded-xl ${colors?.bg || 'bg-slate-100'} ${colors?.dark?.bg || 'dark:bg-slate-800'} border ${colors?.border || 'border-slate-200'} ${colors?.dark?.border || 'dark:border-slate-700'} flex items-center gap-4`}>
                            {integration.avatar_url ? (
                                <img
                                    src={integration.avatar_url}
                                    alt={integration.account_name}
                                    className="w-12 h-12 rounded-full border-2 border-white dark:border-slate-700 shadow"
                                />
                            ) : (
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colors?.bg || 'bg-slate-200'} ${colors?.dark?.bg || 'dark:bg-slate-700'}`}>
                                    <Icon className={`w-6 h-6 ${colors?.text || 'text-slate-600'} ${colors?.dark?.text || 'dark:text-slate-400'}`} />
                                </div>
                            )}
                            <div>
                                <p className="font-semibold text-slate-900 dark:text-white">
                                    {integration.display_name || integration.account_name}
                                </p>
                                <p className={`text-sm ${colors?.text || 'text-slate-600'} ${colors?.dark?.text || 'dark:text-slate-400'}`}>
                                    {PLATFORMS[platform]?.name || platform}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Metrics cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <MetricCard
                            label="Total Views"
                            value={analytics?.views}
                            change={analytics?.views_change}
                            icon={Eye}
                            loading={loading}
                        />
                        <MetricCard
                            label="Likes"
                            value={analytics?.likes}
                            change={analytics?.likes_change}
                            icon={Heart}
                            loading={loading}
                        />
                        <MetricCard
                            label="Comments"
                            value={analytics?.comments}
                            change={analytics?.comments_change}
                            icon={MessageCircle}
                            loading={loading}
                        />
                        <MetricCard
                            label="Shares"
                            value={analytics?.shares}
                            change={analytics?.shares_change}
                            icon={Share2}
                            loading={loading}
                        />
                        <MetricCard
                            label="Followers"
                            value={analytics?.followers}
                            change={analytics?.followers_change}
                            icon={Users}
                            loading={loading}
                        />
                        <MetricCard
                            label="Engagement Rate"
                            value={analytics?.engagement_rate ? `${analytics.engagement_rate}%` : undefined}
                            change={analytics?.engagement_change}
                            icon={TrendingUp}
                            loading={loading}
                        />
                    </div>

                    {/* Info note */}
                    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Analytics are refreshed every 24 hours. Percentage changes are compared to the previous period.
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

export default SocialAnalytics;
